import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin'
import { mergeRegister } from '@lexical/utils'
import { Box, FormHelperText, InputLabel, styled, useFormControl } from '@mui/material'
import {
    $createLineBreakNode,
    $createParagraphNode,
    $createTextNode,
    $addUpdateTag,
    $getRoot,
    $getSelection,
    $isRangeSelection,
    COMMAND_PRIORITY_HIGH,
    COMMAND_PRIORITY_LOW,
    INSERT_LINE_BREAK_COMMAND,
    INSERT_PARAGRAPH_COMMAND,
    KEY_ENTER_COMMAND,
    PASTE_COMMAND,
    PASTE_TAG,
    type EditorState,
} from 'lexical'
import { useEffect, useRef, type CSSProperties, type FocusEventHandler, type KeyboardEventHandler, type ReactNode, type Ref } from 'react'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { ReferenceLexicalPlugin } from './ReferenceLexical'
import { ReferenceNode } from './ReferenceNode'
import type { ReferenceChoice } from './referenceModel'

type ReferencePlainEditorProps = {
    value: string
    onChange: (value: string) => void
    onBlur?: FocusEventHandler<HTMLDivElement>
    onFocus?: FocusEventHandler<HTMLDivElement>
    onKeyDown?: KeyboardEventHandler<HTMLDivElement>
    choices: ReferenceChoice[]
    label?: string
    ariaLabel: string
    ariaLabeledBy?: string
    name: string
    placeholder?: string
    disabled?: boolean
    required?: boolean
    autoFocus?: boolean
    errorMessage?: string
    hasError?: boolean
    size?: 'small' | 'medium'
    inputFieldStyles?: CSSProperties
    maxRows?: string | number
    resize?: CSSProperties['resize']
    textAlign?: CSSProperties['textAlign']
    className?: string
    endAdornment?: ReactNode
    unit?: string
    singleLine?: boolean
    inputRef?: Ref<HTMLDivElement>
    shrinkLabel?: boolean
}

function $setPlainText(text: string) {
    const root = $getRoot()
    root.clear()
    const paragraph = $createParagraphNode()
    text.split('\n').forEach((part, index) => {
        if (index > 0) {
            paragraph.append($createLineBreakNode())
        }
        if (part) {
            paragraph.append($createTextNode(part))
        }
    })
    root.append(paragraph)
    paragraph.selectEnd()
}

function PlainValuePlugin({
    value,
    onChange,
    disabled,
    singleLine,
}: Pick<ReferencePlainEditorProps, 'value' | 'onChange' | 'disabled' | 'singleLine'>) {
    const [editor] = useLexicalComposerContext()
    const lastValue = useRef(value)

    useEffect(() => {
        editor.setEditable(!disabled)
        if (disabled) {
            editor.blur()
        }
    }, [disabled, editor])

    useEffect(() => {
        if (lastValue.current === value) {
            return
        }
        lastValue.current = value
        const current = editor.getEditorState().read(() => $getRoot().getTextContent())
        if (current !== value) {
            editor.update(() => $setPlainText(value))
        }
    }, [editor, value])

    useEffect(() => {
        if (!singleLine) {
            return
        }
        return mergeRegister(
            editor.registerCommand(
                KEY_ENTER_COMMAND,
                (event) => {
                    event?.preventDefault()
                    return true
                },
                COMMAND_PRIORITY_LOW
            ),
            editor.registerCommand(INSERT_PARAGRAPH_COMMAND, () => true, COMMAND_PRIORITY_LOW),
            editor.registerCommand(INSERT_LINE_BREAK_COMMAND, () => true, COMMAND_PRIORITY_LOW),
            editor.registerCommand(
                PASTE_COMMAND,
                (event) => {
                    const text = 'clipboardData' in event ? event.clipboardData?.getData('text/plain') : undefined
                    const selection = $getSelection()
                    if (text === undefined || !$isRangeSelection(selection)) {
                        return false
                    }
                    event.preventDefault()
                    $addUpdateTag(PASTE_TAG)
                    selection.insertText(text.replace(/[\r\n]+/g, ' '))
                    return true
                },
                COMMAND_PRIORITY_HIGH
            )
        )
    }, [editor, singleLine])

    const handleChange = (editorState: EditorState) => {
        const nextValue = editorState.read(() => $getRoot().getTextContent())
        const normalizedValue = singleLine ? nextValue.replace(/[\r\n]+/g, ' ') : nextValue
        if (normalizedValue !== lastValue.current) {
            lastValue.current = normalizedValue
            onChange(normalizedValue)
        }
    }

    return <OnChangePlugin ignoreSelectionChange onChange={handleChange} />
}

export function ReferencePlainEditor({
    value,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    choices,
    label,
    ariaLabel,
    ariaLabeledBy,
    name,
    placeholder,
    disabled,
    required,
    autoFocus,
    errorMessage,
    hasError,
    size = 'medium',
    inputFieldStyles,
    maxRows,
    resize = 'vertical',
    textAlign,
    className,
    endAdornment,
    unit,
    singleLine = false,
    inputRef,
    shrinkLabel,
}: ReferencePlainEditorProps) {
    const formControl = useFormControl()
    const onFilled = formControl?.onFilled
    const onEmpty = formControl?.onEmpty
    const onFormControlBlur = formControl?.onBlur
    const onFormControlFocus = formControl?.onFocus

    useEffect(() => {
        if (value.length > 0) {
            onFilled?.()
        } else {
            onEmpty?.()
        }
    }, [onEmpty, onFilled, value])

    return (
        <LexicalComposer
            initialConfig={{
                namespace: 'ReferencePlainEditor',
                nodes: [ReferenceNode],
                editable: !disabled,
                editorState: () => $setPlainText(value),
                onError: (error: Error) => {
                    throw error
                },
            }}
        >
            {label && (
                <ReferenceInputLabel $disabled={!!disabled} htmlFor={name} shrink={shrinkLabel}>
                    {label}
                </ReferenceInputLabel>
            )}
            <EditorBox $hasError={!!hasError} $disabled={!!disabled} $size={size} $singleLine={singleLine} className={className}>
                <PlainTextPlugin
                    contentEditable={
                        <StyledEditable
                            ref={inputRef}
                            $singleLine={singleLine}
                            id={name}
                            aria-label={ariaLabeledBy ? undefined : ariaLabel}
                            aria-labelledby={ariaLabeledBy}
                            aria-multiline={!singleLine}
                            aria-invalid={hasError || undefined}
                            aria-required={required || undefined}
                            aria-disabled={disabled || undefined}
                            onBlur={(event) => {
                                onFormControlBlur?.(event)
                                onBlur?.(event)
                            }}
                            onFocus={(event) => {
                                onFormControlFocus?.(event)
                                onFocus?.(event)
                            }}
                            onKeyDown={onKeyDown}
                            style={{
                                ...inputFieldStyles,
                                textAlign,
                                maxHeight: singleLine ? '30px' : maxRows ? `${Number(maxRows) * 1.5}em` : inputFieldStyles?.maxHeight,
                                resize: singleLine ? 'none' : resize,
                            }}
                        />
                    }
                    placeholder={<Placeholder>{placeholder}</Placeholder>}
                    ErrorBoundary={LexicalErrorBoundary}
                />
                {(unit || endAdornment) && (
                    <Adornment>
                        {unit}
                        {endAdornment}
                    </Adornment>
                )}
            </EditorBox>
            {!singleLine && (
                <FormHelperText variant="outlined" margin="dense" error sx={{ fontSize: '0.5rem' }}>
                    {errorMessage || ' '}
                </FormHelperText>
            )}
            <PlainValuePlugin value={value} onChange={onChange} disabled={disabled} singleLine={singleLine} />
            <HistoryPlugin />
            <ReferenceLexicalPlugin choices={choices} />
            {autoFocus && <AutoFocusPlugin />}
        </LexicalComposer>
    )
}

const EditorBox = styled(Box, {
    shouldForwardProp: (prop) => !['$hasError', '$disabled', '$size', '$singleLine'].includes(String(prop)),
})<{
    $hasError: boolean
    $disabled: boolean
    $size: 'small' | 'medium'
    $singleLine: boolean
}>(({ $hasError, $disabled, $size, $singleLine }) => ({
    ...($singleLine ? tokens.TextS : {}),
    backgroundColor: $disabled ? tokens.ColorSurfaceDisabled : tokens.ColorBackgroundLight,
    border: `${tokens.SizingBorderS} solid ${$hasError ? tokens.ColorBorderDanger : tokens.ColorBorderDefault}`,
    borderRadius: tokens.SizingCornerS,
    minHeight: $singleLine ? '46px' : $size === 'small' ? '40px' : '72px',
    padding: `${tokens.SizingSpacing075} ${tokens.SizingSpacing150}`,
    position: 'relative',
    overflow: 'auto',
    '&:focus-within': { borderColor: tokens.ColorBorderFocused },
}))

const ReferenceInputLabel = styled(InputLabel, { shouldForwardProp: (prop) => prop !== '$disabled' })<{ $disabled: boolean }>(
    ({ $disabled }) => ({
        zIndex: 1,
        '&.MuiInputLabel-shrink': {
            backgroundColor: $disabled ? tokens.ColorSurfaceDisabled : tokens.ColorBackgroundLight,
            paddingInline: tokens.SizingSpacing050,
        },
    })
)

const StyledEditable = styled(ContentEditable, { shouldForwardProp: (prop) => prop !== '$singleLine' })<{
    $singleLine: boolean
}>(({ $singleLine }) => ({
    minHeight: $singleLine ? '30px' : '56px',
    overflowX: $singleLine ? 'auto' : undefined,
    overflowY: $singleLine ? 'hidden' : 'auto',
    outline: 'none',
    whiteSpace: $singleLine ? 'nowrap' : 'pre-wrap',
    '& > p': { margin: 0 },
}))

const Placeholder = styled(Box)(() => ({
    color: tokens.ColorTextSubtle,
    left: tokens.SizingSpacing150,
    pointerEvents: 'none',
    position: 'absolute',
    top: tokens.SizingSpacing075,
}))

const Adornment = styled(Box)(() => ({
    color: tokens.ColorTextSubtle,
    position: 'absolute',
    right: tokens.SizingSpacing100,
    bottom: tokens.SizingSpacing075,
}))
